import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";

const Container = ({ children, className = "" }) => (
  <div className={`relative mx-auto w-full max-w-[1288px] px-4 sm:px-6 lg:px-8 ${className}`}>
    {children}
  </div>
);

const PROGRAMS = [
  "Digital Money and Stablecoins",
  "Digital Asset Accounting",
  "Business Payments and Treasury",
  "Compliance for Digital Finance Operations",
  "Digital Asset Audit and Assurance",
  "Not sure yet",
];

/* Map program-page slugs to select values for ?program= preselection */
const PROGRAM_SLUGS = {
  "digital-money-and-stablecoins": "Digital Money and Stablecoins",
  "digital-asset-accounting": "Digital Asset Accounting",
  "business-payments-and-treasury": "Business Payments and Treasury",
  "compliance-for-digital-finance-operations":
    "Compliance for Digital Finance Operations",
  "digital-asset-audit-and-assurance": "Digital Asset Audit and Assurance",
};

/* Map ?type= slugs to enrollment-type values for preselection */
const TYPE_SLUGS = {
  membership: "Professional Membership",
  standard: "Standard Annual Access",
  corporate: "Corporate/team enrollment",
};

const ENROLLMENT_TYPES = [
  "Standard Annual Access",
  "Professional Membership",
  "Corporate/team enrollment",
  "Other",
];

const MEMBER_STATUS = ["Yes", "No", "Not yet, but I am interested in membership"];

const BACKGROUNDS = [
  "Finance/accounting",
  "Bookkeeping",
  "Treasury/payments",
  "Compliance/risk",
  "Audit/assurance",
  "Business owner/founder",
  "Fintech/technology",
  "Education/research",
  "Other",
];

/* ================= Hero ================= */

const ApplyHero = () => (
  <section data-testid="apply-hero-section">
    <Container className="pt-[41px] sm:pt-[49px] lg:pt-[90px]">
      <div className="mx-auto max-w-[62rem] text-center">
        <div className="flex items-center justify-center gap-3" aria-hidden="true">
          <span className="h-px w-10 bg-[hsl(var(--primary))]/25" />
          <span className="h-[3px] w-3 bg-[hsl(var(--accent))]" />
          <span className="text-xs font-medium uppercase tracking-[0.22em] text-muted-foreground">
            Program Enrollment
          </span>
          <span className="h-[3px] w-3 bg-[hsl(var(--accent))]" />
          <span className="h-px w-10 bg-[hsl(var(--primary))]/25" />
        </div>
        <h1 className="font-serif-editorial mx-auto mt-8 max-w-4xl text-[2.4rem] font-semibold leading-[1.08] tracking-[-0.03em] text-foreground [text-wrap:balance] sm:text-5xl lg:text-[3.6rem]">
          Start Your Learning Journey in Modern Finance
        </h1>
        <p className="mx-auto mt-8 max-w-[64ch] text-base leading-relaxed text-muted-foreground sm:text-lg lg:text-xl">
          Complete the form below to apply for a WFII program beginning in
          January 2027. Applications close December 23, 2026.
        </p>
        <p className="mx-auto mt-4 max-w-[64ch] text-base leading-relaxed text-muted-foreground sm:text-lg lg:text-xl">
          Our team will review your submission and follow up with the next
          steps, including program access, pricing, and membership options.
        </p>
      </div>
    </Container>
  </section>
);

/* ================= Application form ================= */

const ApplicationForm = () => {
  const location = useLocation();
  const slugValue = PROGRAM_SLUGS[location.state?.program] || "";
  const typeValue =
    TYPE_SLUGS[location.state?.type] ||
    (location.state?.program ? "Standard Annual Access" : "");
  const [program, setProgram] = useState(slugValue);
  const [enrollmentType, setEnrollmentType] = useState(typeValue);
  const [memberStatus, setMemberStatus] = useState("");
  const [background, setBackground] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const isMembershipInquiry = enrollmentType === "Professional Membership";

  useEffect(() => {
    if (slugValue) setProgram(slugValue);
  }, [slugValue]);

  useEffect(() => {
    if (typeValue) setEnrollmentType(typeValue);
  }, [typeValue]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isMembershipInquiry && !program) {
      toast.error("Please select a program of interest.");
      return;
    }
    if (!enrollmentType) {
      toast.error("Please select an enrollment type.");
      return;
    }
    if (!memberStatus) {
      toast.error("Please tell us if you are currently a WFII member.");
      return;
    }
    if (!background) {
      toast.error("Please select the option that best describes your background.");
      return;
    }
    const form = e.target;
    const data = new FormData(form);
    setSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/enrollment-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          program,
          enrollment_type: enrollmentType,
          full_name: data.get("fullName"),
          email: data.get("email"),
          organization: data.get("organization") || "",
          job_title: data.get("jobTitle") || "",
          country: data.get("country"),
          member_status: memberStatus,
          background,
          goals: data.get("goals"),
        }),
      });
      if (!res.ok) throw new Error("Request failed");
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Something went wrong while sending your request.", {
        description: "Please try again in a moment or email us directly.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section data-testid="apply-success-section">
        <Container className="pb-[60px] lg:pb-[120px]">
          <div className="mx-auto max-w-2xl rounded-2xl bg-card/80 p-10 text-center shadow-[0_16px_40px_-24px_hsl(218_51%_15%/0.3)] ring-1 ring-[hsl(var(--primary))]/10 backdrop-blur-[2px] sm:p-14">
            <CheckCircle2
              className="mx-auto h-12 w-12 text-[hsl(var(--accent))]"
              strokeWidth={1.5}
              aria-hidden="true"
            />
            <h2
              className="font-serif-editorial mt-7 text-2xl font-semibold tracking-[-0.02em] text-foreground sm:text-3xl"
              data-testid="apply-success-heading"
            >
              Thank you for your enrollment request.
            </h2>
            <p
              className="mx-auto mt-4 max-w-[46ch] text-base leading-relaxed text-muted-foreground sm:text-lg"
              data-testid="apply-success-message"
            >
              Your submission has been received. The WFII team will review your
              information and follow up with the next steps.
            </p>
            <div className="mt-9 flex flex-wrap items-center justify-center gap-x-8 gap-y-4">
              <Button
                asChild
                className="h-12 bg-[hsl(var(--accent))] px-8 text-sm font-medium text-white shadow-[0_10px_24px_-8px_hsl(174_86%_30%/0.45)] transition-colors hover:bg-primary active:scale-[0.98]"
                data-testid="apply-success-explore-button"
              >
                <Link to="/programs">Explore Programs</Link>
              </Button>
              <Link
                to="/"
                className="group inline-flex h-12 items-center gap-2 text-sm font-medium text-foreground transition-colors hover:text-[hsl(var(--accent))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md"
                data-testid="apply-success-home-link"
              >
                Return Home
                <ArrowRight
                  className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
                  strokeWidth={1.75}
                />
              </Link>
            </div>
          </div>
        </Container>
      </section>
    );
  }

  return (
    <section data-testid="apply-form-section">
      <Container className="pb-[60px] lg:pb-[120px]">
        <form
          onSubmit={handleSubmit}
          className="mx-auto max-w-[880px] rounded-2xl bg-card/80 p-7 shadow-[0_16px_40px_-24px_hsl(218_51%_15%/0.3)] ring-1 ring-[hsl(var(--primary))]/10 backdrop-blur-[2px] sm:p-10 lg:p-12"
          data-testid="apply-form"
        >
          <div className="grid gap-7">
            {/* Program of interest */}
            <div className="grid gap-2">
              <Label htmlFor="program" className="text-sm font-medium text-foreground">
                Program of Interest
                {isMembershipInquiry && (
                  <span
                    className="ml-2 text-xs font-normal text-muted-foreground"
                    data-testid="apply-form-program-optional-note"
                  >
                    (not required for membership inquiries)
                  </span>
                )}
              </Label>
              <Select value={program} onValueChange={setProgram}>
                <SelectTrigger
                  id="program"
                  className="h-11 bg-background"
                  data-testid="apply-form-program-trigger"
                >
                  <SelectValue placeholder="Select a program" />
                </SelectTrigger>
                <SelectContent>
                  {PROGRAMS.map((p) => (
                    <SelectItem
                      key={p}
                      value={p}
                      data-testid={`apply-form-program-option-${p.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    >
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Enrollment type */}
            <div className="grid gap-3">
              <Label className="text-sm font-medium text-foreground">Enrollment Type</Label>
              <RadioGroup
                value={enrollmentType}
                onValueChange={setEnrollmentType}
                className="grid gap-2.5 sm:grid-cols-2"
                data-testid="apply-form-enrollment-type-group"
              >
                {ENROLLMENT_TYPES.map((t) => (
                  <Label
                    key={t}
                    className={`flex cursor-pointer items-center gap-3 rounded-lg border px-4 py-3 text-sm font-normal transition-colors ${
                      enrollmentType === t
                        ? "border-[hsl(var(--accent))] bg-[hsl(174_45%_40%/0.06)] text-foreground"
                        : "border-[hsl(var(--primary))]/15 bg-background text-foreground/80 hover:border-[hsl(var(--primary))]/30"
                    }`}
                    data-testid={`apply-form-enrollment-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  >
                    <RadioGroupItem value={t} />
                    {t}
                  </Label>
                ))}
              </RadioGroup>
              {isMembershipInquiry && (
                <div
                  className="mt-1 rounded-lg bg-[hsl(174_45%_40%/0.06)] p-4 text-sm leading-relaxed text-foreground/80"
                  data-testid="apply-form-membership-note"
                >
                  <p>
                    Applications are subject to review. Annual membership fee:
                    CAD $39 upon approval.
                  </p>
                  <p className="mt-2">
                    WFII membership is application-based and designed for
                    professionals who want to stay connected to new programs,
                    resources, and industry developments.
                  </p>
                </div>
              )}
            </div>

            {/* Identity fields */}
            <div className="grid gap-7 sm:grid-cols-2 sm:gap-6">
              <div className="grid gap-2">
                <Label htmlFor="full-name" className="text-sm font-medium text-foreground">
                  Full Name
                </Label>
                <Input
                  id="full-name"
                  name="fullName"
                  required
                  autoComplete="name"
                  placeholder="Your full name"
                  className="h-11 bg-background"
                  data-testid="apply-form-name-input"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="email" className="text-sm font-medium text-foreground">
                  Email Address
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="you@company.com"
                  className="h-11 bg-background"
                  data-testid="apply-form-email-input"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="organization" className="text-sm font-medium text-foreground">
                  Organization/Company
                </Label>
                <Input
                  id="organization"
                  name="organization"
                  autoComplete="organization"
                  placeholder="Company or organization"
                  className="h-11 bg-background"
                  data-testid="apply-form-organization-input"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="job-title" className="text-sm font-medium text-foreground">
                  Job Title/Role
                </Label>
                <Input
                  id="job-title"
                  name="jobTitle"
                  autoComplete="organization-title"
                  placeholder="Your role"
                  className="h-11 bg-background"
                  data-testid="apply-form-job-title-input"
                />
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="country" className="text-sm font-medium text-foreground">
                Country
              </Label>
              <Input
                id="country"
                name="country"
                required
                autoComplete="country-name"
                placeholder="Your country"
                className="h-11 bg-background"
                data-testid="apply-form-country-input"
              />
            </div>

            {/* Member status */}
            <div className="grid gap-3">
              <Label className="text-sm font-medium text-foreground">
                Are you currently a WFII member?
              </Label>
              <RadioGroup
                value={memberStatus}
                onValueChange={setMemberStatus}
                className="grid gap-2.5"
                data-testid="apply-form-member-status-group"
              >
                {MEMBER_STATUS.map((s) => (
                  <Label
                    key={s}
                    className={`flex cursor-pointer items-center gap-3 rounded-lg border px-4 py-3 text-sm font-normal transition-colors ${
                      memberStatus === s
                        ? "border-[hsl(var(--accent))] bg-[hsl(174_45%_40%/0.06)] text-foreground"
                        : "border-[hsl(var(--primary))]/15 bg-background text-foreground/80 hover:border-[hsl(var(--primary))]/30"
                    }`}
                    data-testid={`apply-form-member-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  >
                    <RadioGroupItem value={s} />
                    {s}
                  </Label>
                ))}
              </RadioGroup>
            </div>

            {/* Background */}
            <div className="grid gap-2">
              <Label htmlFor="background" className="text-sm font-medium text-foreground">
                What best describes your background?
              </Label>
              <Select value={background} onValueChange={setBackground}>
                <SelectTrigger
                  id="background"
                  className="h-11 bg-background"
                  data-testid="apply-form-background-trigger"
                >
                  <SelectValue placeholder="Select your background" />
                </SelectTrigger>
                <SelectContent>
                  {BACKGROUNDS.map((b) => (
                    <SelectItem
                      key={b}
                      value={b}
                      data-testid={`apply-form-background-option-${b.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    >
                      {b}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Goals */}
            <div className="grid gap-2">
              <Label htmlFor="goals" className="text-sm font-medium text-foreground">
                What are you hoping to learn or achieve through this program?
              </Label>
              <Textarea
                id="goals"
                name="goals"
                required
                rows={6}
                placeholder="Tell us what you hope to understand, improve, or apply after completing this program."
                className="min-h-[140px] resize-y bg-background"
                data-testid="apply-form-goals-textarea"
              />
            </div>

            <p
              className="text-xs leading-relaxed text-muted-foreground"
              data-testid="apply-form-consent-note"
            >
              *By submitting this form, you agree that WFII may contact you
              about your enrollment request, program access, membership
              options,{" "}
              <br className="hidden lg:block" aria-hidden="true" />
              and related educational resources.
            </p>

            <div>
              <Button
                type="submit"
                disabled={submitting}
                className="h-12 bg-[hsl(var(--accent))] px-8 text-sm font-medium text-white shadow-[0_10px_24px_-8px_hsl(174_86%_30%/0.45)] transition-colors hover:bg-primary active:scale-[0.98] disabled:opacity-60"
                data-testid="apply-form-submit-button"
              >
                {submitting ? "Submitting..." : "Submit Application"}
              </Button>
            </div>
          </div>
        </form>
      </Container>
    </section>
  );
};

/* ================= Page ================= */

export default function ApplyPage() {
  return (
    <div className="relative min-h-screen">
      <div className="wfii-canvas wfii-noise" aria-hidden="true" data-testid="page-canvas" />
      <div className="relative z-10 flex min-h-screen flex-col">
        <Header />
        <main className="flex-1 space-y-[30px] lg:space-y-[60px]">
          <ApplyHero />
          <ApplicationForm />
        </main>
        <Footer />
        <Toaster position="bottom-right" />
      </div>
    </div>
  );
}
